import { useState, useCallback, useEffect, useMemo } from 'react';
import type { UserGamification, UserAchievement, Achievement } from '@/types';
import {
  getUserGamification,
  getUserAchievements,
  getAchievements,
  subscribeToGamification,
  subscribeToAchievements,
} from '@/lib/habits-api';

interface UseGamificationReturn {
  stats: UserGamification | null;
  achievements: Achievement[];
  unlockedAchievements: UserAchievement[];
  recentUnlocks: UserAchievement[];
  isLoading: boolean;
  error: string | null;
  refreshStats: () => Promise<void>;
  clearRecentUnlocks: () => void;
  // Computed values
  xpToNextLevel: number;
  levelProgress: number;
  categoryProgress: {
    faith: { xp: number; percentage: number };
    family: { xp: number; percentage: number };
    finance: { xp: number; percentage: number };
    fitness: { xp: number; percentage: number };
  };
}

// XP required to reach a given level: (level - 1)^2 x 100
function xpForLevel(level: number): number {
  return Math.pow(level - 1, 2) * 100;
}

// XP needed to go from current level to next level
function xpBetweenLevels(currentLevel: number): number {
  return xpForLevel(currentLevel + 1) - xpForLevel(currentLevel);
}

export function useGamification(): UseGamificationReturn {
  const [stats, setStats] = useState<UserGamification | null>(null);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [unlockedAchievements, setUnlockedAchievements] = useState<UserAchievement[]>([]);
  const [recentUnlocks, setRecentUnlocks] = useState<UserAchievement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all gamification data
  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [gamificationData, allAchievements, userAchievements] = await Promise.all([
        getUserGamification(),
        getAchievements(),
        getUserAchievements(),
      ]);
      setStats(gamificationData);
      setAchievements(allAchievements);
      setUnlockedAchievements(userAchievements);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch gamification stats');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Subscribe to realtime gamification updates
  useEffect(() => {
    const unsubscribe = subscribeToGamification('current-user', (updatedStats) => {
      setStats(updatedStats);
    });

    return unsubscribe;
  }, []);

  // Subscribe to new achievement unlocks
  useEffect(() => {
    const unsubscribe = subscribeToAchievements('current-user', (newUnlock) => {
      // Add to unlocked achievements
      setUnlockedAchievements((prev) => [newUnlock, ...prev]);
      // Add to recent unlocks for notifications
      setRecentUnlocks((prev) => [newUnlock, ...prev]);
    });

    return unsubscribe;
  }, []);

  // Clear recent unlocks (call after showing notification)
  const clearRecentUnlocks = useCallback(() => {
    setRecentUnlocks([]);
  }, []);

  // Computed: XP needed to reach next level
  const xpToNextLevel = useMemo(() => {
    if (!stats) return 100;
    const nextLevelXp = xpForLevel(stats.currentLevel + 1);
    return nextLevelXp - stats.totalXp;
  }, [stats]);

  // Computed: Progress percentage to next level (0-100)
  const levelProgress = useMemo(() => {
    if (!stats) return 0;
    const currentLevelXp = xpForLevel(stats.currentLevel);
    const xpIntoLevel = stats.totalXp - currentLevelXp;
    const xpNeeded = xpBetweenLevels(stats.currentLevel);
    return Math.min(100, Math.round((xpIntoLevel / xpNeeded) * 100));
  }, [stats]);

  // Computed: Category progress (for radar chart or balance display)
  const categoryProgress = useMemo(() => {
    if (!stats) {
      return {
        faith: { xp: 0, percentage: 0 },
        family: { xp: 0, percentage: 0 },
        finance: { xp: 0, percentage: 0 },
        fitness: { xp: 0, percentage: 0 },
      };
    }

    // Find the max category XP to calculate relative percentages
    const maxXp = Math.max(
      stats.faithXp,
      stats.familyXp,
      stats.financeXp,
      stats.fitnessXp,
      1 // Prevent division by zero
    );

    return {
      faith: {
        xp: stats.faithXp,
        percentage: Math.round((stats.faithXp / maxXp) * 100),
      },
      family: {
        xp: stats.familyXp,
        percentage: Math.round((stats.familyXp / maxXp) * 100),
      },
      finance: {
        xp: stats.financeXp,
        percentage: Math.round((stats.financeXp / maxXp) * 100),
      },
      fitness: {
        xp: stats.fitnessXp,
        percentage: Math.round((stats.fitnessXp / maxXp) * 100),
      },
    };
  }, [stats]);

  return {
    stats,
    achievements,
    unlockedAchievements,
    recentUnlocks,
    isLoading,
    error,
    refreshStats: fetchStats,
    clearRecentUnlocks,
    xpToNextLevel,
    levelProgress,
    categoryProgress,
  };
}
